import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Avatar,
  Typography,
  IconButton,
} from '@mui/material';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useRouteLoaderData } from 'react-router-dom';
import files from '../../../services/FileService';
import auth from '../../../services/AuthService';
import createNewFile from '../../../services/utils/createNewFile';
import ToastMessage from '@/components/toasts/ToastMessage';

export default function ProfileForm() {
  const { me } = useRouteLoaderData('auth');

  const [profile, setProfile] = useState({
    firstName: me?.firstName || '',
    lastName: me?.lastName || '',
    phone: me?.mobilePhone || '',
    profilePicture: me?.profilePicture,
    email: me?.email || '',
  });

  const [profilePictureFile, setProfilePictureFile] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await uploadProfilePicture(file);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      await uploadProfilePicture(file);
    }
  };

  const uploadProfilePicture = async (file) => {
    const profilePictureId = await createNewFile(me);
    const url = `/${profilePictureId}`;
    try {
      await files.post(url, { file });
      await auth.put(
        '/me/profilePicture',
        JSON.stringify({ profilePicture: { fileId: profilePictureId } }),
      );
      setProfilePictureFile(file);
      setProfile({ ...profile, profilePicture: profilePictureId });
      toast.success(
        <ToastMessage title="Erfolg" subtitle="Profilbild aktualisiert" />,
      );
    } catch (e) {
      toast.error(
        <ToastMessage title="Fehler" subtitle={e?.response?.data?.message} />,
      );
    }
  };

  const handleEditClick = () => {
    document.getElementById('profilePictureInput').click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const deleteProfilePicture = async () => {
    const { profilePicture } = profile;
    const url = `/${profilePicture}`;
    try {
      await files.post(`/delete${url}`);
      await auth.put(
        '/me/profilePicture',
        JSON.stringify({ profilePicture: null }),
      );
      setProfilePictureFile(null);
      setProfile({ ...profile, profilePicture: null });
      toast.success(
        <ToastMessage title="Erfolg" subtitle="Profil aktualisiert" />,
      );
    } catch (e) {
      toast.error(
        <ToastMessage title="Fehler" subtitle={e?.response?.data?.message} />,
      );
    }
  };

  const handleSubmit = async () => {
    const updateObject = {
      firstName: profile.firstName,
      lastName: profile.lastName,
      phone: profile.phone,
    };
    try {
      await auth.put('/me', JSON.stringify(updateObject));
      toast.success(
        <ToastMessage title="Erfolg" subtitle="Profil aktualisiert" />,
      );
    } catch (e) {
      toast.error(
        <ToastMessage title="Fehler" subtitle={e?.response?.data?.message} />,
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <>
      <Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
        Profil bearbeiten
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            position: 'relative',
            width: 150,
            height: 150,
            marginBottom: 2,
            '&:hover .avatar-actions': {
              opacity: 1,
            },
          }}
        >
          <Avatar
            src={
              profilePictureFile
                ? URL.createObjectURL(profilePictureFile)
                : me.profilePicture
                  ? `${import.meta.env.VITE_FILE_URL}/${me.profilePicture}`
                  : ''
            }
            sx={{ width: '100%', height: '100%' }}
          />

          <Box
            className="avatar-actions"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent:
                me.profilePicture || profile.profilePicture
                  ? 'space-around'
                  : 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '50%',
              opacity: 0,
              transition: 'opacity 0.3s',
            }}
          >
            {me.profilePicture || profile.profilePicture ? (
              <>
                <IconButton
                  onClick={handleEditClick}
                  sx={{
                    color: 'white',
                  }}
                >
                  <EditIcon />
                </IconButton>

                <IconButton
                  onClick={deleteProfilePicture}
                  sx={{
                    color: 'white',
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </>
            ) : (
              <IconButton
                onClick={handleEditClick}
                sx={{
                  color: 'white',
                  fontSize: '2rem',
                }}
              >
                <AddIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Hidden file input */}
        <input
          id="profilePictureInput"
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
      <TextField
        fullWidth
        label="Vorname"
        name="firstName"
        value={profile?.firstName}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      <TextField
        fullWidth
        label="Nachname"
        name="lastName"
        value={profile?.lastName}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      <TextField
        fullWidth
        label="E-Mail"
        name="email"
        value={profile?.email}
        disabled
      />
      <TextField
        fullWidth
        label="Telefonnummer"
        name="phone"
        value={profile?.phone}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      <Button
        variant="contained"
        color="primary"
        fullWIdth
        onClick={handleSubmit}
      >
        Änderungen speichern
      </Button>
    </>
  );
}
